import React from 'react';
import { AuthInputTextField, AuthInputNumberField, AuthSeparator } from '@/components';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@design-system';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';

interface DefaultsTabProps {
  advisorState: AdvisorProfile;
  errors: Partial<Record<keyof AdvisorProfile, boolean>>;
  handleAdvisorStateChange: (
    advisorKey: keyof AdvisorProfile,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAdvisorSelectChange: (advisorKey: keyof AdvisorProfile) => (value: string) => void;
}

export function DefaultsTab({
  advisorState,
  errors,
  handleAdvisorStateChange,
  handleAdvisorSelectChange,
}: DefaultsTabProps) {
  const { flags } = useFeatureFlags();

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
      <AuthSeparator
        className="sm:col-span-6"
        text="Healthcare Inflation Settings"
      />
      <AuthInputNumberField
        id="defaultHealthcareInflationPercent"
        description="The default healthcare inflation rate used to calculate the client's healthcare costs."
        label="Default Healthcare Inflation Rate"
        value={advisorState.defaultHealthcareInflationPercent}
        onChange={handleAdvisorStateChange('defaultHealthcareInflationPercent')}
        placeholder="e.g. 5.4"
        unit="%"
        error={errors.defaultHealthcareInflationPercent}
        className="mt-0 sm:col-span-6"
        min={0}
        step={0.001}
        max={30}
        width="150px"
        required={false}
      />
      <AuthInputTextField
        id="defaultHealthcareInflationSourceText"
        label="Default Healthcare Inflation Source Text"
        description="Displayed text for the healthcare inflation source citation."
        value={advisorState.defaultHealthcareInflationSourceText ?? ''}
        onChange={handleAdvisorStateChange('defaultHealthcareInflationSourceText')}
        className="mt-0 sm:col-span-6"
        required={Boolean(advisorState.defaultHealthcareInflationPercent)}
        error={errors.defaultHealthcareInflationSourceText}
      />
      <AuthInputTextField
        id="defaultHealthcareInflationSourceUrl"
        label="Default Healthcare Inflation Source Link"
        description="URL for the healthcare inflation rate source."
        value={advisorState.defaultHealthcareInflationSourceUrl ?? ''}
        onChange={handleAdvisorStateChange('defaultHealthcareInflationSourceUrl')}
        className="mt-0 sm:col-span-6"
        required={false}
      />

      {flags.ltcCostRoundingOptions && (
        <>
          <AuthSeparator
            className="sm:col-span-6"
            text="Projected cost display"
          />
          <div className="sm:col-span-6">
            <label
              htmlFor="ltcCostDisplayPrecision"
              className="mb-2 block text-sm font-medium text-gray-800"
            >
              Projected cost display
            </label>
            <p className="mb-3 text-sm text-gray-700">
              Choose how projected LTC costs are shown in the app and PDFs: precise amount or
              rounded to the nearest $10, $100, or $1,000. When rounded, hover to see the precise
              amount.
            </p>
            <Select
              value={advisorState.ltcCostDisplayPrecision ?? 'precise'}
              onValueChange={handleAdvisorSelectChange('ltcCostDisplayPrecision')}
            >
              <SelectTrigger className="w-64" id="ltcCostDisplayPrecision">
                <SelectValue placeholder="Select display type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="precise">Precise</SelectItem>
                <SelectItem value="10">Round to the Nearest 10</SelectItem>
                <SelectItem value="100">Round to the Nearest 100</SelectItem>
                <SelectItem value="1000">Round to the Nearest 1000</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}
    </div>
  );
}
